/**
 * Dashboard auth helpers — BFF opaque session only (no legacy /v2/auth login).
 */
import {
  fetchBffCurrentUser,
  fetchBffSession,
  logoutBffSession,
  startBffLogin,
  type BffProfile,
} from "./bff-auth.helper";

const ALLOWED_ROLES = ["super_admin", "admin"] as const;

export type DashboardSession = {
  id: string;
  email: string;
  role: (typeof ALLOWED_ROLES)[number];
  onboardingCompleted: boolean;
};

function isAllowedRole(role: string): role is (typeof ALLOWED_ROLES)[number] {
  return (ALLOWED_ROLES as readonly string[]).includes(role);
}

function toSession(profile: BffProfile): DashboardSession | null {
  if (!isAllowedRole(profile.role)) return null;
  return {
    id: profile.id,
    email: profile.email,
    role: profile.role,
    onboardingCompleted: Boolean(profile.onboardingCompleted),
  };
}

export async function validateSession(): Promise<DashboardSession | null> {
  const session = await fetchBffSession();
  if (!session) return null;
  const profile = await fetchBffCurrentUser();
  if (!profile) return null;
  return toSession(profile);
}

/** Re-probe BFF session + profile (replaces legacy cookie refresh). */
export async function refreshSession(): Promise<DashboardSession | null> {
  return validateSession();
}

export async function logout(): Promise<void> {
  await logoutBffSession({ global: false });
}

/** Legacy name used by header chrome — BFF local logout. */
export const logoutUser = logout;

export async function loginRedirect(returnTo = "/overview"): Promise<void> {
  startBffLogin(returnTo);
}

export { startBffLogin, logoutBffSession, fetchBffCurrentUser, fetchBffSession };
